import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowLeft, Home, Grid3x3, X } from "lucide-react";
import { API_URL, resolveAsset } from "../lib/brand";

export default function MagazineReader() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [issue, setIssue] = useState(null);
  const [pageIdx, setPageIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showThumbs, setShowThumbs] = useState(false);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/magazines/${slug}`)
      .then((r) => {
        setIssue(r.data);
        document.title = `${r.data.title} · ${r.data.month_label} — Sfoglia`;
      })
      .catch(() => setIssue(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const pages = issue?.pages || [];
  const total = pages.length;

  const goto = useCallback((next) => {
    setDirection(next > pageIdx ? 1 : -1);
    setPageIdx(Math.max(0, Math.min(total - 1, next)));
  }, [pageIdx, total]);

  const prev = useCallback(() => goto(pageIdx - 1), [pageIdx, goto]);
  const next = useCallback(() => goto(pageIdx + 1), [pageIdx, goto]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") setShowThumbs(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next]);

  // Preload adjacent pages for smooth flipping
  useEffect(() => {
    [pageIdx + 1, pageIdx + 2].forEach((i) => {
      if (i < total) {
        const img = new Image();
        img.src = resolveAsset(pages[i]);
      }
    });
  }, [pageIdx, pages, total]);

  if (loading) {
    return <div className="min-h-screen bg-[#3D2140] flex items-center justify-center text-white/60 font-editorial italic text-xl">Sto aprendo il numero…</div>;
  }
  if (!issue) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center p-6 text-center">
        <div>
          <p className="font-editorial italic text-xl text-[#3D2140]/70 mb-4">Numero non trovato</p>
          <Link to="/magazine" className="text-[#FF2768] font-semibold hover:underline">← Torna al magazine</Link>
        </div>
      </div>
    );
  }

  const variants = {
    enter: (dir) => ({ opacity: 0, x: dir * 60, rotateY: dir * 6 }),
    center: { opacity: 1, x: 0, rotateY: 0 },
    exit: (dir) => ({ opacity: 0, x: dir * -60, rotateY: dir * -6 }),
  };

  return (
    <div className="min-h-screen bg-[#3D2140] text-white" data-testid="magazine-reader">
      {/* Header bar */}
      <header className="sticky top-0 z-30 bg-[#3D2140]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link to="/magazine" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#FFE45C]" data-testid="reader-back">
            <ArrowLeft size={16} /> Magazine
          </Link>
          <div className="text-center flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FFE45C]">— {issue.number} · {issue.month_label}</p>
            <p className="font-display italic text-xl text-white truncate">{issue.title}</p>
          </div>
          <button
            onClick={() => setShowThumbs(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm border border-white/15"
            data-testid="reader-thumbs-btn"
            title="Tutte le pagine"
          >
            <Grid3x3 size={14} /> <span className="hidden sm:inline">{pageIdx + 1} / {total}</span>
          </button>
        </div>
      </header>

      {/* Page viewer */}
      <div className="max-w-[1600px] mx-auto px-4 py-8 md:py-12 flex flex-col items-center">
        <div className="relative w-full max-w-[900px]" style={{ perspective: "2000px" }}>
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={pageIdx}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              className="rounded-2xl overflow-hidden shadow-2xl bg-white"
              data-testid="reader-page"
            >
              <img
                src={resolveAsset(pages[pageIdx])}
                alt={`Pagina ${pageIdx + 1} di ${total}`}
                className="w-full h-auto block"
                loading="eager"
              />
            </motion.div>
          </AnimatePresence>

          {/* Prev/Next hitboxes */}
          <button
            onClick={prev}
            disabled={pageIdx === 0}
            className="absolute left-0 top-0 h-full w-1/4 opacity-0 hover:opacity-100 focus:opacity-100 disabled:cursor-not-allowed flex items-center justify-start pl-2 md:pl-4 transition-opacity"
            aria-label="Pagina precedente"
            data-testid="reader-prev-hover"
          >
            <span className="p-3 rounded-full bg-white/90 text-[#3D2140] shadow-xl">
              <ChevronLeft size={24} />
            </span>
          </button>
          <button
            onClick={next}
            disabled={pageIdx === total - 1}
            className="absolute right-0 top-0 h-full w-1/4 opacity-0 hover:opacity-100 focus:opacity-100 disabled:cursor-not-allowed flex items-center justify-end pr-2 md:pr-4 transition-opacity"
            aria-label="Pagina successiva"
            data-testid="reader-next-hover"
          >
            <span className="p-3 rounded-full bg-white/90 text-[#3D2140] shadow-xl">
              <ChevronRight size={24} />
            </span>
          </button>
        </div>

        {/* Persistent controls (mobile-friendly) */}
        <div className="mt-8 flex items-center gap-4">
          <button
            onClick={prev}
            disabled={pageIdx === 0}
            className="p-3 rounded-full bg-white/15 hover:bg-[#FF2768] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="reader-prev"
            aria-label="Precedente"
          >
            <ChevronLeft size={20} />
          </button>
          <p className="text-sm text-white/80 tabular-nums min-w-[80px] text-center" data-testid="reader-counter">
            <span className="text-[#FFE45C] font-bold">{pageIdx + 1}</span> / {total}
          </p>
          <button
            onClick={next}
            disabled={pageIdx === total - 1}
            className="p-3 rounded-full bg-white/15 hover:bg-[#FF2768] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="reader-next"
            aria-label="Successiva"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="mt-6 w-full max-w-[900px] h-1 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-[#FF2768]"
            initial={false}
            animate={{ width: `${((pageIdx + 1) / total) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        <p className="mt-6 text-xs text-white/40 text-center">
          Usa le frecce ← → della tastiera oppure clicca ai lati della pagina per sfogliare
        </p>
      </div>

      {/* THUMBS PANEL */}
      <AnimatePresence>
        {showThumbs && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#3D2140]/95 backdrop-blur p-4 md:p-10 overflow-y-auto"
            onClick={() => setShowThumbs(false)}
            data-testid="reader-thumbs-panel"
          >
            <div className="max-w-[1400px] mx-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#FFE45C]">— Tutte le pagine</p>
                  <p className="font-display italic text-3xl text-white">{issue.number} · {issue.month_label}</p>
                </div>
                <button onClick={() => setShowThumbs(false)} className="p-2 rounded-full bg-white/10 hover:bg-white/20" data-testid="reader-thumbs-close">
                  <X size={18} />
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {pages.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => { setPageIdx(i); setShowThumbs(false); }}
                    className={`group text-left rounded-xl overflow-hidden border-2 transition-colors ${
                      i === pageIdx ? "border-[#FF2768] ring-2 ring-[#FF2768]/30" : "border-white/15 hover:border-[#FFE45C]"
                    }`}
                    data-testid={`reader-thumb-${i}`}
                  >
                    <img src={resolveAsset(p)} alt={`Pagina ${i + 1}`} className="w-full h-auto block" loading="lazy" />
                    <p className="p-2 bg-black/40 text-xs text-white/80 text-center">Pagina {i + 1}</p>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
